import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { InfoSheetPanel, useOverlay, H3, Subheading, Separator } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import ZionForm, { useZionForm } from '@fs/zion-form'
import { useFeatureFlag } from '@fs/zion-flags'
import {
  generateSls,
  getNestedOrFloatingElementById,
  getNestedSubelementsByType,
  getOtherPersonId,
  getPersonName,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import EventFormFields from './EventFormFields'
import DeleteEventButton from './DeleteEventButton'
import PanelHeader from '../shared/PanelHeader'
import SaveComponent from '../shared/SaveComponent'
import { createEventDispatches, parseEvent } from './utils/eventUtils'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import useR2RelationshipOptions from '../../../../views/indexing/hooks/useR2RelationshipOptions'
import useUnsavedChangesOverlay from '../../../../hooks/useUnsavedChangesOverlay'
import SharedEvent from './selectPersonsCheckboxes/SharedEvent'
import Tip from '../../../tip/Tip'
import { validate, generateFaqList, buildEventFieldHighlights } from './utils/EventPanel'

/**
 * Callback function for the onSave event data.
 *
 * @callback onSaveCallback
 * @param {Object[]} dispatches - An array of dispatches.
 * @param {Object[]} formValues - The form values object from the event panel.
 */

/**
 * Callback function for the onDelete event data.
 *
 * @callback onDeleteCallback
 * @param {Object[]} dispatches - An array of dispatches.
 * @param {String} eventId - The event id to be deleted.
 */

/**
 * EventPanel Component - Renders an event panel for SLS Person or chips from the Full Text View.
 * It provides functionalities for saving changes, deleting events, and managing event fields.
 *
 * @component
 * @param {Object} props - The component properties.
 * @param {Object} [props.person] - The person object associated with the event.
 * @param {Object|null} [props.event] - The event object to be managed. Defaults to null if no event is provided.
 * @param {onSaveCallback} props.onSave - Callback function to be called when saving changes to the event. Defaults to a no-op function.
 * @param {onDeleteCallback} props.onDelete - Callback function to be called when deleting the event. Defaults to a no-op function.
 * @param {String} [props.splitScreenImageArk] - The image ark for the split screen.
 * @param {Object} props.infoSheetPanelProps - Additional properties to be passed to the info sheet panel.
 * @param {Boolean} [props.shouldParseEventfromSls=true] - Flag to determine if the event should be parsed from SLS. Defaults to true.
 * @param {Boolean} [props.shouldSavetoSls=true] - Flag to determine if the event should be saved to SLS. Defaults to true.
 * @param {Boolean} [props.shouldSaveHighlights=false] - Flag to determine if the panel should handle the save of the highlights.
 * @param {Boolean} [props.showRelationshipEvents=true] - Flag to determine if the relationship event types should be shown in the selector.
 * @param {String} [props.personName] - The name of the person to be displayed in the panel header. If not passed name will be inferred from the person object.
 * @param {String} [props.userId] - The user id to be used for saving the event data.
 * @param {Function} [props.viewInContextHandler] - The function to be called to view the persons on other image in the split screen. (Passed on to SharedEvent)
 * @param {Boolean} [props.useSharedEvent=false] - Flag to determine if the shared event panel should be used.
 * @param {String} [props.appName] - The name of the app using this component. This is a required parameter.
 * @returns {React.ReactElement} The EventPanel component.
 */
const EventPanel = ({
  person = null,
  event = null,
  onSave = () => {},
  onDelete = () => {},
  splitScreenImageArk = '',
  infoSheetPanelProps,
  shouldParseEventfromSls = true,
  shouldSavetoSls = true,
  showRelationshipEvents = true,
  personName: initialPersonName,
  shouldSaveHighlights = false,
  userId = '',
  viewInContextHandler,
  useSharedEvent = false,
  appName,
}) => {
  if (!appName) throw new Error('EventPanel: appName is required parameter')

  const [t] = useTranslation()
  const { eventPanelOverlay, highlightsPanelOldOverlay, openOverlay, closeOverlay, topMostPanel, inPanelStack } =
    useOverlayContext()
  const saveChangesOverlay = useOverlay()
  const { setDisplayHighlights, getHighlights, getDispatches, clearEdits, commitEdits } = useSlsHighlightsContext()
  const { types, currentImageArk, images } = useSlsStateContext()
  const [stashedDispatches, setStashedDispatches] = useState([])
  const linkExistingEventFlag = useFeatureFlag('rip_sharedImageViewer_linkExistingEvent')

  // Getting data for the related persons
  const relationships =
    shouldParseEventfromSls && showRelationshipEvents ? getNestedSubelementsByType(person, 'RELATIONSHIP') : []
  const parentRelationship =
    relationships.length > 0
      ? relationships.find((rel) => event?.superElements?.find((supEl) => supEl.id === rel.id))
      : null
  const relationshipOptions = useR2RelationshipOptions({
    person,
    currentPersonRecord: getNestedOrFloatingElementById(images, person?.recordId),
  })?.filter((option) => option.key !== person?.id)
  const otherPersonId = useMemo(
    () => (parentRelationship ? getOtherPersonId(parentRelationship, person) : null),
    [parentRelationship, person]
  )
  const selectedRelationshipPerson = useMemo(
    () => (otherPersonId ? relationshipOptions.find((option) => option.key === otherPersonId) : null),
    [relationshipOptions, otherPersonId]
  )

  const initialDefaultValues = useMemo(
    () => {
      return parseEvent({
        event,
        types,
        t,
        selectedRelationshipPerson,
        parentRelationship,
        showRelationshipEvents,
        shouldParseEventfromSls,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event, shouldParseEventfromSls, types, t, eventPanelOverlay.isOpen, selectedRelationshipPerson, parentRelationship]
  )
  const formMethods = useZionForm({
    mode: 'all',
    resolver: (values) => validate(values, t),
    shouldFocusError: false,
    defaultValues: initialDefaultValues,
  })

  useUnsavedChangesOverlay({ methods: formMethods, overlay: saveChangesOverlay })

  const { getValues, reset, formState } = formMethods
  const personName = useMemo(
    () => initialPersonName ?? (!person?.id.includes('1:1:TEMP-ID-') ? getPersonName({ person }) : person?.label),
    [person, initialPersonName]
  )
  const [eventTypeField, newEvent, addedEventId] = getValues(['eventTypeField', 'newEvent', 'id'])

  const eventFaq = generateFaqList(t, useSharedEvent, openOverlay)

  useEffect(() => {
    if (eventPanelOverlay.isOpen && topMostPanel() === 'eventPanel') {
      formMethods.reset(initialDefaultValues)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, eventPanelOverlay.isOpen, initialDefaultValues])

  const getHighlightsToDisplay = useCallback(
    () => buildEventFieldHighlights(getValues, getHighlights),
    [getHighlights, getValues]
  )

  const highlightEventFields = useCallback(() => {
    setDisplayHighlights({
      mainPartitionKey: currentImageArk,
      splitScreenPartitionKey: splitScreenImageArk,
      highlights: getHighlightsToDisplay(),
    })
  }, [currentImageArk, getHighlightsToDisplay, setDisplayHighlights, splitScreenImageArk])

  useEffect(() => {
    if (eventPanelOverlay.isOpen && !highlightsPanelOldOverlay.isOpen) {
      highlightEventFields()
    }
  }, [eventPanelOverlay.isOpen, highlightsPanelOldOverlay.isOpen, highlightEventFields])

  const resetAndClose = useCallback(() => {
    reset() // Resets the react hook form
    closeOverlay('eventPanel') // Uses overlay hook to close itself
    if (inPanelStack('linkExistingEvent')) {
      closeOverlay('linkExistingEvent')
    }
    clearEdits()
  }, [reset, closeOverlay, inPanelStack, clearEdits])

  const handleClose = useCallback(() => {
    if (formState.isDirty) {
      saveChangesOverlay.open() // Opens save changes overlay
    } else {
      resetAndClose()
    }
  }, [saveChangesOverlay, formState.isDirty, resetAndClose])

  const onSaveSharedEvent = (dispatches) => {
    setStashedDispatches(dispatches)
  }

  const onSubmit = (values) => {
    const highlightDispatches = shouldSaveHighlights
      ? getDispatches({ mainImageArk: currentImageArk, splitScreenImageArk })
      : []

    const dispatches = createEventDispatches(person, values, types, formState, images, true, appName)
    const completeDispatches = [...dispatches, ...stashedDispatches, ...highlightDispatches]
    const sls = generateSls(completeDispatches)

    return { sls, dispatches: completeDispatches }
  }

  return (
    <>
      <ZionForm methods={formMethods} onSubmit={onSubmit} id="edit-events">
        <InfoSheetPanel
          nonClosable
          headingColor="green"
          title={t('edit-index.edit-person.heading', 'Edit {personName}', {
            personName,
          })}
          footer={
            <SaveComponent
              saveChangesOverlay={saveChangesOverlay}
              onSubmit={onSubmit}
              finished={(dispatches) => {
                onSave(dispatches, getValues())
                commitEdits()
                resetAndClose()
              }}
              canceled={resetAndClose}
              formName="edit-events"
              personId={userId}
              shouldSavetoSls={shouldSavetoSls}
              appName={appName}
              saveInitiatorFilename="EventPanel.js"
            />
          }
          {...eventPanelOverlay}
          {...infoSheetPanelProps}
          close={handleClose}
        >
          <PanelHeader
            heading={t('edit-event.heading-generic', 'Event')}
            caption={t(
              'edit-events.instructions',
              'Fix any fields that are incorrect including their highlights. Add or remove fields as necessary.'
            )}
          />
          {linkExistingEventFlag.isOn && (
            <Tip
              primaryText={t('edit-event.help-header', 'Help with Events')}
              listPrimaryText={
                <>
                  {eventFaq.map((faq) => (
                    <div key={faq.key}>
                      <Separator size="xs" />
                      <H3 size="H6">
                        {faq.key}. {faq.question}
                        <Subheading>{faq.answer}</Subheading>
                      </H3>
                    </div>
                  ))}
                  <Separator size="sm" />
                </>
              }
            />
          )}
          <EventFormFields
            person={person}
            personName={personName}
            relationshipOptions={relationshipOptions}
            showRelationshipEvents={showRelationshipEvents}
            useSharedEvent={useSharedEvent}
          />

          {event && (
            <DeleteEventButton
              fieldLabel={eventTypeField?.primaryText || eventTypeField?.label}
              personId={person?.personId}
              event={event}
              onSuccess={(dispatches) => {
                onDelete(dispatches, getValues().id)
                resetAndClose()
              }}
              shouldSaveToSls={shouldSavetoSls}
              appName={appName}
            />
          )}
        </InfoSheetPanel>
      </ZionForm>
      {inPanelStack('sharedEvent') && (
        <SharedEvent
          selectedPerson={person}
          event={event}
          userId={userId}
          viewInContextHandler={viewInContextHandler}
          onSave={onSaveSharedEvent}
          shouldSaveToSls={!newEvent}
          addedEventId={addedEventId}
          appName={appName}
        />
      )}
    </>
  )
}

export default EventPanel
